/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { CurrencyEnum } from './CurrencyEnum';
import type { File } from './File';
import type { Languages } from './Languages';
import type { ProjectInvitation } from './ProjectInvitation';
import type { ProjectMatchedManufacturer } from './ProjectMatchedManufacturer';
import type { ProjectPart } from './ProjectPart';
import type { Quote } from './Quote';

export type Project = {
  id: number;
  created_at: string;
  updated_at: string;
  title: string;
  incoterms: Project.incoterms | null;
  /**
   * Currencies accepted for quotes
   */
  currencies: Array<CurrencyEnum>;
  /**
   * External Id of the Project (optional)
   */
  projectId: string | null;
  deadline: string;
  deliveryDate: string | null;
  /**
   * Is the file protected by NDA. Boolean.
   */
  isNdaProtected: boolean;
  /**
   * Is the file private and not present in the feed. Boolean.
   */
  isPrivate: boolean;
  /**
   * Is the RFQ created by an ADMIN on behalf of a Company. Boolean.
   */
  isOnBehalf: boolean;
  note: string | null;
  /**
   * The reason why buyer is creating project. This is an enum.
   */
  reasonForRequest: Project.reasonForRequest;
  /**
   * The languages of the project. 2 letters (en, de, it...) defaults to ['en'].
   */
  languages: Array<Languages>;
  /**
   * Can be either ACTIVE or CLOSED
   */
  status: Project.status;
  /**
   * The id of the company that created the project
   */
  companyId: number;
  /**
   * The id of the user that created the project
   */
  userId: number | null;
  additionalFiles: Array<File>;
  parts: Array<ProjectPart>;
  invitations: Array<ProjectInvitation>;
  /**
   * Certifications required to take on this project
   */
  certifications: Array<string>;
  /**
   * Tender area for filtering manufacturers by location
   */
  tenderArea: Array<string>;
  zipFile: File | null;
  ndaFile: File | null;
  isSniping?: boolean;
  lastInvitationBatchAt?: string | null;
  lastUserUpdateAt?: string | null;
  /**
   * Certifications required to take on this project
   */
  matchedManufacturers?: Array<ProjectMatchedManufacturer>;
  daysToDeadline?: number;
  /**
   * The mode of the project. Can be either single_parts or assemblies.
   */
  mode: Project.mode;
  feedbackDeadline?: string | null;
  quotes: Array<Quote>;
  statusUpdatedAt: string;
  /**
   * Whether the project can provide feedback. Boolean. Returns true if the project is closed, has valid invitations, no quotes, and is within the feedback period.
   */
  canProvideFeedback: boolean;
};

export namespace Project {
  export enum incoterms {
    CFR = 'CFR',
    CIF = 'CIF',
    CIP = 'CIP',
    CPT = 'CPT',
    DAP = 'DAP',
    DAT = 'DAT',
    DDP = 'DDP',
    DPU = 'DPU',
    EXW = 'EXW',
    FAS = 'FAS',
    FCA = 'FCA',
    FOB = 'FOB',
  }

  /**
   * The reason why buyer is creating project. This is an enum.
   */
  export enum reasonForRequest {
    BENCHMARKING = 'BENCHMARKING',
    NEW_PRODUCT = 'NEW_PRODUCT',
    SECOND_SOURCE = 'SECOND_SOURCE',
    SUPPLIER_FAILURE = 'SUPPLIER_FAILURE',
  }

  /**
   * Can be either ACTIVE or CLOSED
   */
  export enum status {
    ACTIVE = 'ACTIVE',
    CLOSED = 'CLOSED',
  }

  /**
   * The mode of the project. Can be either single_parts or assemblies.
   */
  export enum mode {
    SINGLE_PARTS = 'single_parts',
    ASSEMBLIES = 'assemblies',
  }
}
